import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
} from 'react-bootstrap';
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaLayerGroup,
  FaUsers,
  FaCalendar,
  FaFilter,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import BatchService from '../../../services/batchService';
import BatchForm from './BatchForm';

const BatchManagement = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    loadBatches();
  }, []);

  useEffect(() => {
    filterBatches();
  }, [batches, searchTerm, statusFilter]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const response = await BatchService.getAllBatches();
      setBatches(response.data || []);
    } catch (error) {
      toast.error('Failed to load batches');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterBatches = () => {
    let filtered = [...batches];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(b =>
        b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.course_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    setFilteredBatches(filtered);
  };

  const handleEdit = (batch) => {
    setSelectedBatch(batch);
    setShowEditModal(true);
  };

  const handleDelete = (batch) => {
    setSelectedBatch(batch);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await BatchService.deleteBatch(selectedBatch.id);
      toast.success('Batch deleted successfully');
      loadBatches();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'upcoming':
        return <Badge bg="info">Upcoming</Badge>;
      case 'completed':
        return <Badge bg="secondary">Completed</Badge>;
      default:
        return <Badge bg="warning">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>
              <FaLayerGroup className="me-2 text-primary" />
              Batch Management
            </h2>
          </Col>
          <Col className="text-end">
            <Button
              variant="success"
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus className="me-2" /> Create New Batch
            </Button>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row>
              <Col md={8}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by batch name or course..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Batches Table */}
        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Batch Name</th>
                    <th>Course</th>
                    <th>Duration</th>
                    <th>Students</th>
                    <th>Completed</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <h5 className="text-muted">No batches found</h5>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowCreateModal(true)}
                          className="mt-2"
                        >
                          Create your first batch
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    filteredBatches.map((batch, index) => (
                      <tr key={batch.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{batch.name}</strong>
                        </td>
                        <td>{batch.course_name}</td>
                        <td>
                          <small>
                            <FaCalendar className="me-1 text-muted" />
                            {formatDate(batch.start_date)} - {formatDate(batch.end_date)}
                          </small>
                        </td>
                        <td className="text-center">
                          <Badge bg="info" pill>
                            <FaUsers className="me-1" />
                            {batch.total_students || 0}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="success" pill>
                            {batch.completed_students || 0}
                          </Badge>
                        </td>
                        <td>{getStatusBadge(batch.status)}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1"
                            onClick={() => navigate(`/admin/batches/${batch.id}`)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEdit(batch)}
                            title="Edit Batch"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(batch)}
                            title="Delete Batch"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Create Batch Modal */}
        <BatchForm
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadBatches();
          }}
        />

        {/* Edit Batch Modal */}
        <BatchForm
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          batch={selectedBatch}
          onSuccess={() => {
            setShowEditModal(false);
            loadBatches();
          }}
        />

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <FaTrash className="me-2" />
              Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedBatch && (
              <>
                <p>Are you sure you want to delete batch:</p>
                <p className="fw-bold text-danger">{selectedBatch.name}</p>
                <p variant="warning">
                  This will also remove all students and materials from this batch.
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Batch
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default BatchManagement;